import type { TooltipFormatterContextObject } from 'highcharts'

export interface TooltipConfig {
  symbol?: string
  currency?: string
  maxItems?: number
  showTotal?: boolean
  dateFormatter?: (timestamp: number) => string
  valueFormatter?: (value: number, symbol?: string) => string
}

export interface TooltipSections {
  containerClass?: string
  header?: string
  date: string
  content: string
  footer?: string
}

export const useChartTooltip = () => {
  // 기본 컨테이너 스타일
  const getDefaultContainer = (customClass?: string) => {
    const defaultClass = `
      z-20
      p-2.5
      text-white
      border rounded-lg bg-white/10
      backdrop-blur-sm
      flex flex-col
      justify-start
      items-start
      gap-2
    `
    const mergedClass = customClass ? `${customClass} ${defaultClass}` : defaultClass
    return `<div style="border-color: rgba(255, 255, 255, 0.2)" class="${mergedClass}">`
  }

  // 헤더 섹션 생성
  const createHeader = (content: string = '') => {
    return content
  }

  // 날짜 섹션 생성
  const createDate = (
    timestamp: number,
    formatter?: (timestamp: number) => string,
  ) => {
    const defaultFormatter = (ts: number) => {
      // DATE_FORMATS.DATE_DEFAULT 형식으로 포맷
      return new Date(ts).toLocaleDateString()
    }
    
    const formatDate = formatter || defaultFormatter
    return `<p><b>${formatDate(timestamp)}</b></p>`
  }

  // 콘텐츠 섹션 생성
  const createContent = (
    points: any[],
    config: TooltipConfig = {},
  ) => {
    const { maxItems = 15, valueFormatter } = config
    
    let content = ''
    points.forEach((point, index) => {
      if (index > maxItems) return
      
      const chartColor = point.series.userOptions.color || point.color
      const dataName = point.series.name
      const formattedValue = valueFormatter 
        ? valueFormatter(point.y, config.symbol)
        : `${point.y} ${config.symbol || ''}`
      
      content += `
        <div style="display: flex; justify-content: space-between; max-width: 300px; width: 100%;">
          <p><span style="color:${chartColor};">● </span>${dataName}</p>
          <p>${formattedValue}</p>
        </div>`
    })
    
    return content
  }

  // 푸터 섹션 생성 (총합)
  const createFooter = (
    points: any[],
    config: TooltipConfig = {},
  ) => {
    if (!config.showTotal) return ''
    
    const total = points.reduce((acc, cur) => cur.y + acc, 0)
    const formattedTotal = config.valueFormatter 
      ? config.valueFormatter(total, config.symbol)
      : `${total} ${config.symbol || ''}`
    
    return `
      <hr class="w-full" />
      <div style="display: flex; justify-content: space-between; max-width: 300px; width: 100%;">
        <p><span style="color: dark">● </span>TOTAL</p>
        <p>${formattedTotal}</p>
      </div>`
  }

  // 전체 tooltip 생성
  const createTooltip = (
    context: TooltipFormatterContextObject,
    config: TooltipConfig = {},
  ) => {
    const points = context.points || []
    
    const sections: TooltipSections = {
      header: createHeader(),
      date: createDate(context.x as number, config.dateFormatter),
      content: createContent(points, config),
      footer: createFooter(points, config),
    }
    
    const html = getDefaultContainer('') +
      sections.header +
      sections.date +
      sections.content +
      (sections.footer ? '<hr class="w-full" style="border-color: rgba(255, 255, 255, 0.2)" />' : '') +
      sections.footer +
      '</div>'
    
    return html
  }

  // 개별 섹션을 커스터마이징할 수 있는 고급 버전
  const createCustomTooltip = (
    context: TooltipFormatterContextObject,
    sectionCreators: {
      containerClass?: string,
      header?: () => string
      date?: (timestamp: number) => string
      content?: (points: any[]) => string
      footer?: (points: any[]) => string
    },
  ) => {
    const sections: TooltipSections = {
      containerClass: sectionCreators.containerClass || '',
      header: sectionCreators.header?.() || '',
      date: sectionCreators.date?.(context.x as number) || '',
      content: sectionCreators.content?.(context.points || context.point || []) || createContent(context.points || []),
      footer: sectionCreators.footer?.(context.points || []) || '',
    }
    
    return getDefaultContainer(sections.containerClass) +
      sections.header +
      sections.date +
      sections.content +
      (sections.footer ? '<hr class="w-full" style="border-color: rgba(255, 255, 255, 0.2)" />' : '') +
      sections.footer +
      '</div>'
  }

  return {
    createTooltip,
    createCustomTooltip,
    createHeader,
    createDate,
    createContent,
    createFooter,
    getDefaultContainer,
  }
} 